import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './FoldersItem.module.scss';
import Col from '../GridSystem/Col';
import { Card } from '../Card';
import CardHeader from '../Card/CardHeader';
import Checkbox from '../Checkbox/Checkbox';
import CardTitle from '../Card/CardTitle';
import { useLibraryContext } from '../../context/LibraryContext';
import DotsBtn from '../Menu/DotsBtn';
import CardMenu from '../CardMenu/CardMenu';
import CardExtra from '../Card/CardExtra';
import CardFooter from '../Card/CardFooter';
import Highlighter from 'react-highlight-words';
import cx from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { FOLDER_USER_ROLE } from '../../constants/UserRoles';
import UsersListPreview from '../UsersListPreview/UsersListPreview';
import { useUserContext } from '../../context/UserContext';
import Tooltip from '../Tooltip/Tooltip';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import { AnimationWrapper } from '../AnimationWrapper/AnimationWrapper';

const FoldersItem = ({
  listMode,
  folder,
  folderIndex,
  handleCardClick,
  handleDotsClick,
  showMenu,
  menuRef,
  toggleShowMenu,
  shareFolder,
  renameFolder,
  duplicateFolder,
  archiveFolder,
  deleteFolder,
  unarchiveFolder,
  renameFolderData,
  onRenameInputChange,
  renameFolderAction,
  folderNameError,
  searchInput,
  isMobileView,
  squeezeCard,
}) => {
  const history = useHistory();

  const { t } = useTranslation();

  const parent = useRef();
  const labelRef = useRef();

  const { selectedFolder, toggleUnselectFolders } = useLibraryContext();

  const [selectedFolderChanged, setSelectedFolderChanged] = useState(false);

  const [hoverFolder, setHoverFolder] = useState({});
  const [titleHover, setTitleHover] = useState({});

  const [init, setInit] = useState(false);

  const { user } = useUserContext();

  const showCheckbox = useMemo(
    () => hoverFolder.isHover && hoverFolder.index === folderIndex && !folder.isPlaceholder && !isMobileView,
    [hoverFolder, folderIndex, folder, isMobileView]
  );

  const isFolderOwner = user.username === folder?.owner?.username;
  const isFolderEditable = !!folder?.editingEnabled;
  const isViewer = folder?.users?.find(
    ({ username, folderRole }) => username === user.username && folderRole === FOLDER_USER_ROLE.VIEWER.TYPE
  );
  const isEditor = folder?.users?.find(
    ({ username, folderRole }) => username === user.username && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE
  );
  const isFolderAdmin = isEditor && isFolderEditable;

  const isUserHasAdvancedEditingRights = isFolderOwner || isFolderAdmin;

  const ownershipLabel = useMemo(() => {
    let role = '';
    if (isViewer) {
      role = 'viewer';
    }
    if (isEditor && !isFolderAdmin) {
      role = 'editor';
    }
    if (isFolderAdmin) {
      role = 'admin';
    }
    if (!!folder?.users?.length && isFolderOwner) {
      role = 'owner';
    }
    return role ? (
      <div ref={labelRef} className={cx(styles[role])}>
        {t(`folder.role.${role}`)}
      </div>
    ) : null;
  }, [isViewer, isEditor, isFolderAdmin, isFolderOwner, folder, t]);

  const isFoldersSelected = useMemo(() => !!Object.values(selectedFolder)?.length, [selectedFolder]);

  const toggleMouseEnterCard = folderIndex => {
    setHoverFolder({ index: folderIndex, isHover: true });
  };

  const toggleMouseLeaveCard = folderIndex => {
    setHoverFolder({ index: folderIndex, isHover: false });
  };

  const toggleMouseEnterTitle = videoIndex => {
    setTitleHover({ index: videoIndex, isHover: true });
  };

  const toggleMouseLeaveTitle = videoIndex => {
    setTitleHover({ index: videoIndex, isHover: false });
  };

  useEffect(() => {
    setInit(true);

    return () => {
      toggleUnselectFolders();
    };
  }, []);

  const onCardClick = useCallback(
    event => {
      if (folder.isPlaceholder) {
        return;
      }

      event.stopPropagation();

      const classList = Array.from(event?.target?.parentElement?.classList).join('');

      if (renameFolderData[folder.id]) {
        return;
      }
      const isTargetCard = classList.includes('Card');
      const isLinkClick = classList.includes('highlighterCard');

      if (isMobileView) {
        if (isTargetCard && !isLinkClick) {
          history.push(`/eg/folder/${folder.id}`);
        }
      } else {
        if (isFoldersSelected) {
          handleCardClick(event, folderIndex);
          return;
        }

        if (isTargetCard && !isLinkClick) {
          history.push(`/eg/folder/${folder.id}`);
        }
      }
      event.stopPropagation();
    },
    [renameFolderData]
  );

  useEffect(() => {
    setSelectedFolderChanged(prevState => !prevState);
  }, [selectedFolder]);

  return (
    <Col xs={12} sm={3} md={3} lg={3} xl={3} className={styles.cards}>
      <Card
        data-cy="video"
        onMouseEnter={() => toggleMouseEnterCard(folderIndex)}
        onMouseLeave={() => toggleMouseLeaveCard(folderIndex)}
        onClick={onCardClick}
        className={cx(styles.folderCard, {
          [styles.activeCard]: selectedFolder[folder.id],
          [styles.errorCard]: folderNameError && renameFolderData && renameFolderData[folder.id],
        })}
      >
        <CardHeader>
          <CardTitle className={styles.relative} innerRef={parent}>
            <div className={cx(styles.absolute, styles.checkbox)}>
              <AnimationWrapper show={showCheckbox || selectedFolder[folder.id]} options={{ duration: 250 }}>
                <Checkbox
                  onClick={event => handleCardClick(event, folderIndex)}
                  checked={selectedFolder[folder.id]}
                  className={styles.folderCheckbox}
                />
              </AnimationWrapper>
            </div>
            <div className={cx(styles.avatar, styles.absolute)}>
              <AnimationWrapper
                show={!showCheckbox && !selectedFolder[folder.id]}
                from={{ opacity: 0, ...(init ? { transform: 'scale(0.5)' } : {}) }}
                to={{ opacity: 1, ...(init ? { transform: 'scale(1)' } : {}) }}
                options={{ duration: 250 }}
                transformOrigin={'top left'}
              >
                <div className={cx(styles.usersDetails)}>
                  <div className={styles.avatars}>
                    <UsersListPreview
                      owner={folder?.owner}
                      user={user}
                      users={folder?.users ?? []}
                      parentRef={parent}
                      maxLeftSpace={shareFolder ? labelRef.current?.offsetWidth : 0}
                      widthChanged={selectedFolderChanged}
                    />
                  </div>
                </div>
              </AnimationWrapper>
              <AnimationWrapper show={!showCheckbox && !selectedFolder[folder.id]} options={{ duration: 250 }}>
                {ownershipLabel}
              </AnimationWrapper>
            </div>
          </CardTitle>
          <CardExtra className={cx(styles.relative, { [styles.cardExtra]: isMobileView })}>
            <AnimationWrapper
              show={
                (((hoverFolder.isHover && hoverFolder.index === folderIndex) || selectedFolder[folder.id]) &&
                  !(renameFolderData && renameFolderData[folder.id])) ||
                isMobileView
              }
              options={{ duration: 250 }}
            >
              <DotsBtn className={styles.dotsButton} tagName="BUTTON" onClick={() => handleDotsClick(folder.id)} />
            </AnimationWrapper>
            {!isMobileView && showMenu?.uuid === folder.id && showMenu.flag ? (
              <CardMenu
                menuRef={menuRef}
                listMode={listMode}
                item={folder}
                itemIndex={folderIndex}
                toggleShowMenu={toggleShowMenu}
                shareFolder={isUserHasAdvancedEditingRights && shareFolder}
                renameItem={isUserHasAdvancedEditingRights && renameFolder}
                duplicateItem={duplicateFolder}
                archiveItem={isFolderOwner && archiveFolder}
                deleteFolder={deleteFolder}
                unarchiveItem={unarchiveFolder}
                rightOffset={true}
              />
            ) : null}
          </CardExtra>
        </CardHeader>
        <CardFooter
          className={cx(styles.footerCard, { [styles.squeezeFooter]: squeezeCard })}
          onMouseEnter={() => toggleMouseEnterTitle(folderIndex)}
          onMouseLeave={() => toggleMouseLeaveTitle(folderIndex)}
        >
          {renameFolderData && renameFolderData[folder.id] ? (
            <input
              autoFocus
              className={cx(styles.rename, { [styles.errorInput]: folderNameError })}
              maxLength={MAX_NAME_LENGTH}
              type="text"
              onChange={e => onRenameInputChange(e, folder.id)}
              value={renameFolderData[folder.id].name}
              onKeyDown={async e => {
                if (e.key === 'Enter') {
                  await renameFolderAction(folder.id);
                  setHoverFolder(false);
                }
              }}
              onBlur={async () => await renameFolderAction(folder.id)}
            />
          ) : (
            <div className={styles.cardTitleContainer}>
              <Link to={`/eg/folder/${folder.id}`} className={styles.link}>
                <CardTitle className={cx(styles.cardTitle, { [styles.squeezeTitle]: squeezeCard })}>
                  <Highlighter
                    highlightClassName="wordHighlighter"
                    searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                    autoEscape
                    textToHighlight={folder.name}
                    className={styles.highlighterCard}
                  />
                </CardTitle>
              </Link>
              <Tooltip isHover={titleHover.isHover} fullwidth={true} moveable={false}>
                <Highlighter
                  highlightClassName="wordHighlighter"
                  searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                  autoEscape
                  textToHighlight={folder.name}
                  className={styles.hoverText}
                />
              </Tooltip>
            </div>
          )}
          <div className={styles.footerExtra}>
            <span className={styles.createdAtField}>{t('general.date', { date: new Date(`${folder.createdAt}`) })}</span>
            {folder.storyCount > 0 && <span> {`| ${folder.storyCount} video${folder.storyCount === 1 ? '' : 's'}`}</span>}
          </div>
        </CardFooter>
      </Card>
      {isMobileView && (
        <Modal open={showMenu?.uuid === folder.id && showMenu.flag} onClose={toggleShowMenu} focusTrapped={false}>
          <div className={styles.menuContainer}>
            <CardMenu
              menuRef={menuRef}
              listMode={listMode}
              item={folder}
              itemIndex={folderIndex}
              toggleShowMenu={toggleShowMenu}
              shareFolder={isUserHasAdvancedEditingRights && shareFolder}
              renameItem={isUserHasAdvancedEditingRights && renameFolder}
              duplicateItem={duplicateFolder}
              archiveItem={isFolderOwner && archiveFolder}
              deleteFolder={deleteFolder}
              unarchiveItem={unarchiveFolder}
            />
          </div>
        </Modal>
      )}
    </Col>
  );
};

export default FoldersItem;
